import { CategoryNode } from '../types/category';

export function cloneNodes(nodes: CategoryNode[]): CategoryNode[] {
  return nodes.map((node) => ({
    ...node,
    children: node.children ? cloneNodes(node.children) : undefined,
  }));
}

export function treesEqual(a: CategoryNode[], b: CategoryNode[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

const newId = () => `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export function createNode(name = 'קטגוריה חדשה'): CategoryNode {
  return { id: newId(), name, children: [] };
}

export function findNodeLevel(nodes: CategoryNode[], id: string, level = 1): number | null {
  for (const node of nodes) {
    if (node.id === id) return level;
    if (node.children) {
      const found = findNodeLevel(node.children, id, level + 1);
      if (found) return found;
    }
  }
  return null;
}

export function updateNodeName(nodes: CategoryNode[], id: string, name: string): CategoryNode[] {
  return nodes.map((node) => {
    if (node.id === id) return { ...node, name };
    if (node.children) return { ...node, children: updateNodeName(node.children, id, name) };
    return node;
  });
}

export function addChildNode(nodes: CategoryNode[], parentId: string | null): CategoryNode[] {
  const child = createNode();
  if (!parentId) return [...nodes, child];
  return nodes.map((node) => {
    if (node.id === parentId) {
      return { ...node, children: [...(node.children || []), child] };
    }
    if (node.children) return { ...node, children: addChildNode(node.children, parentId) };
    return node;
  });
}

export function addNamedChild(
  nodes: CategoryNode[],
  parentId: string | null,
  name: string,
  noVat: boolean,
): CategoryNode[] {
  const child: CategoryNode = { id: newId(), name, children: [] };
  if (noVat) child.no_vat = true;
  if (!parentId) return [...nodes, child];
  return nodes.map((node) => {
    if (node.id === parentId) {
      return { ...node, children: [...(node.children || []), child] };
    }
    if (!node.children) return node;
    return { ...node, children: addNamedChild(node.children, parentId, name, noVat) };
  });
}

export function sortCategoryNodes(nodes: CategoryNode[]): CategoryNode[] {
  return [...nodes]
    .sort((a, b) => a.name.localeCompare(b.name, 'he', { sensitivity: 'base', numeric: true }))
    .map((node) =>
      node.children?.length ? { ...node, children: sortCategoryNodes(node.children) } : node,
    );
}

export function parentOptions(
  nodes: CategoryNode[],
  level: 1 | 2,
): { id: string; label: string }[] {
  const sorted = sortCategoryNodes(nodes);
  if (level === 1) {
    return sorted.map((node) => ({ id: node.id, label: node.name }));
  }
  return sorted
    .flatMap((parent) =>
      (parent.children || []).map((child) => ({
        id: child.id,
        label: parent.name ? `${parent.name} — ${child.name}` : child.name,
      })),
    )
    .sort((a, b) => a.label.localeCompare(b.label, 'he', { sensitivity: 'base', numeric: true }));
}

export function ancestorIds(nodes: CategoryNode[], id: string, path: string[] = []): string[] | null {
  for (const node of nodes) {
    if (node.id === id) return path;
    if (node.children) {
      const found = ancestorIds(node.children, id, [...path, node.id]);
      if (found) return found;
    }
  }
  return null;
}

export function deleteNode(nodes: CategoryNode[], id: string): CategoryNode[] {
  return nodes
    .filter((node) => node.id !== id)
    .map((node) =>
      node.children ? { ...node, children: deleteNode(node.children, id) } : node
    );
}

export function moveNode(nodes: CategoryNode[], id: string, direction: -1 | 1): CategoryNode[] {
  const index = nodes.findIndex((node) => node.id === id);
  if (index >= 0) {
    const next = index + direction;
    if (next < 0 || next >= nodes.length) return nodes;
    const copy = [...nodes];
    const current = copy[index];
    copy[index] = copy[next];
    copy[next] = current;
    return copy;
  }
  return nodes.map((node) =>
    node.children ? { ...node, children: moveNode(node.children, id, direction) } : node
  );
}

export function updateNodeNoVat(nodes: CategoryNode[], id: string, noVat: boolean): CategoryNode[] {
  return nodes.map((node) => {
    const children = node.children ? updateNodeNoVat(node.children, id, noVat) : node.children;
    if (node.id !== id) {
      return children === node.children ? node : { ...node, children };
    }
    const next: CategoryNode = { ...node, children };
    if (noVat) {
      next.no_vat = true;
    } else {
      delete next.no_vat;
    }
    return next;
  });
}
